import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UtilityService } from '../../service/utility/utility.service';
import { Priveleges } from '../../lib/model/priveleges'
import { ContractsCommonService } from '../../service/contract-creation/contracts-common.service';
import { TableService } from '../../service/table/table.service';
import { PortType, DndType, PerDiemType, ContractStatus } from '../../lib/enums/contracts.enum';

interface ColumnSetting {
  header: string;
  field: string;
  isDisabled?: boolean;
  isVisible: boolean;
  order: number;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone :false
})
export class TableComponent {
  @Input() tableTitle: string = ''; // Table Title
  @Input() tableHeaders: any[] = []; // Table Columns Configuration
  @Input() tableData: any[] = []; // Data to display
  @Output() deleteRow = new EventEmitter<number>(); // Emit Row Index on Delete
  @Output() saveRow = new EventEmitter<any>(); // Emit Row Data on Save
  @Output() cancelEdit = new EventEmitter<any>(); // Emit Row Data on Cancel
  @Output() editRow = new EventEmitter<any>();
  @Output() refreshTable = new EventEmitter<void>();
  @Output() downloadTableData = new EventEmitter<boolean>();
  originalData: any[] = []; // To track unedited data for cancel operation
  privilege: typeof Priveleges;
  displayTableDialog: boolean = false;
  companyName: string = '';
  auditData: any;
  columns: any[] = [];
  selectedColumns: ColumnSetting[] = [];
  constructor(private utilityService: UtilityService, private contractCommonService: ContractsCommonService, private tableService: TableService) {
    this.privilege = Priveleges;
    this.columns = this.tableService.getContractCreateTableColumns();
    this.selectedColumns = this.getSortedColumns(
      this.columns.filter(col => col.isVisible)
    );
  }

  private getSortedColumns(columns: ColumnSetting[]): ColumnSetting[] {
    return [...columns].sort((a, b) => a.order - b.order);
  }

  updateColumnVisibility(event: any) {
    console.log('Event:', event);
    if (this.selectedColumns.length === 0) {
      this.selectedColumns = [...this.getSortedColumns(
        this.columns.filter(col => col.isVisible)
      )];
      return;
    }

    // Update visibility while maintaining order
    this.columns.forEach(header => {
      header.isVisible = this.selectedColumns.some(col => col.field === header.field);
    });

    // Ensure columns are in correct order
    this.selectedColumns = this.getSortedColumns(this.selectedColumns);
    this.columns = this.getSortedColumns(this.columns);
  }

  ngOnInit() {
    // Save original data for cancel edits
    // this.originalData = JSON.parse(JSON.stringify(this.tableData));
  }

  viewDetailedLogs(rowData: any): void {
    this.auditData = [];
    if (!rowData || !rowData.companyName || !rowData.contractId) {
      console.error('Invalid row data provided:', rowData);
      return;
    }
    this.companyName = rowData.companyName;
    this.contractCommonService.getDetailedLogs(rowData.contractId).subscribe({
      next: (response) => {
        if (response && response.data && response.data.length > 0) {
          console.log('Detailed Logs:', response);
          this.displayTableDialog = true;
          this.auditData = this.transformTableData(response.data).sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime());
        } else {
          console.warn('No detailed logs found for contractId:', rowData.contractId);
          this.displayTableDialog = false;
        }
      },
      error: (error) => {
        this.displayTableDialog = false;
        console.error('Error fetching detailed logs:', error);
      }
    });
  }

  transformTableData(data: any[]): any[] {
    return data.map(row => {
      const transformedRow = {
        ...row,
        portType: row?.portType === PortType.ORIGIN ? 'Origin' : row?.portType === PortType.DESTINATION ? 'Destination' : '',
        dndType: row?.dndType === DndType.COMBINED ? 'Combined' : row?.dndType === DndType.SEPARATE ? 'Separate' : '',
        isHazardous: row?.isHazardous ? 'Hazardous' : row?.isHazardous === false ? 'Non-Hazardous' : '',
        perDiemChargesType: row?.perDiemChargesType === PerDiemType.FLAT ? 'Flat' : row?.perDiemChargesType === PerDiemType.TIER ? 'Tier' : '',
        demurrageFreeDays: row?.demurrageFreeDays ?? 'NA',
        detentionFreeDays: row?.detentionFreeDays ?? 'NA',
        dndCombinedFreeDays: row?.dndCombinedFreeDays ?? 'NA',
      };
      // Process tiers dynamically
      if (Array.isArray(row?.tiers)) {
        row.tiers.forEach((tier: any) => {
          const type = tier.tierType === 0 ? 'dnd' : tier.tierType === 1 ? 'detention' : tier.tierType === 2 ? 'demurrage' : ''; // e.g., 'detention', 'demurrage', 'dnd'
          const tierNumber = tier.tierNumber; // e.g., 1, 2, 3...
          transformedRow[`${type}Tier${tierNumber}PerDiemCharges`] = tier.perDiemCharges;
          transformedRow[`${type}Tier${tierNumber}StartDay`] = tier.startDay;
          transformedRow[`${type}Tier${tierNumber}EndDay`] = tier.endDay;
        });
      }
      return transformedRow;
    });
  }

  shouldHighlight(field: string): boolean {
    return field === 'endDate'; // Apply yellow highlight only to these fields
  }

  onRowEditInit(rowData: any) {
    this.editRow.emit(rowData);
    console.log('Editing row started:', rowData);
  }

  onRowEditSave(rowData: any, rowIndex: number) {
    console.log('Row data saved:', rowData);
    this.saveRow.emit({ rowData, rowIndex });
  }

  onRowEditCancel(rowData: any, rowIndex: number) {
    // Restore original data for the canceled row
    this.tableData[rowIndex] = { ...this.originalData[rowIndex] };
    this.cancelEdit.emit(rowData);
  }

  hasFeatureAccess(feature: string, privilege: string): boolean {
    return this.utilityService.hasPrivilege(feature, privilege);
  }

  onRowDelete(rowIndex: number) {
    this.tableData.splice(rowIndex, 1); // Remove row from table
    this.deleteRow.emit(rowIndex);
    console.log('Row deleted:', rowIndex);
  }

  onRefreshTable() {
    this.refreshTable.emit();
  }

  onDownloadTableData() {
    let hasData: boolean = this.tableData && this.tableData.length > 0;
    this.downloadTableData.emit(hasData);
  }
}
